// Location utility for automatic address and country detection
export interface LocationDetails {
  full_address: string;
  country_name: string;
  country_code: string;
  city: string;
  state: string;
  postal_code: string;
  latitude: number;
  longitude: number;
}

export interface LocationError {
  type: 'permission_denied' | 'position_unavailable' | 'timeout' | 'api_error' | 'unknown';
  message: string;
}

// Get user's current location and reverse geocode to get address details
export const getCurrentLocationDetails = async (): Promise<LocationDetails | LocationError> => {
  try {
    // Check if geolocation is supported
    if (!navigator.geolocation) {
      return {
        type: 'position_unavailable',
        message: 'Geolocation is not supported by this browser'
      };
    }

    // Get user's coordinates
    const position = await new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        resolve,
        reject,
        {
          enableHighAccuracy: true,
          timeout: 10000, // 10 seconds
          maximumAge: 300000 // 5 minutes
        }
      );
    });

    const { latitude, longitude } = position.coords;
    console.log('Location coordinates:', { latitude, longitude });

    // Reverse geocode using OpenStreetMap Nominatim API
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
      {
        headers: {
          'User-Agent': 'HomeCookt/1.0 (https://homecookt.com)',
          'Accept': 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Geocoding API error: ${response.status}`);
    }

    const data = await response.json();
    console.log('Reverse geocoding response:', data);

    if (!data || !data.address) {
      return {
        type: 'api_error',
        message: 'Could not determine address from your location'
      };
    }

    const address = data.address;
    
    // Extract address components
    const house_number = address.house_number || '';
    const road = address.road || address.street || '';
    const suburb = address.suburb || address.neighbourhood || '';
    const city = address.city || address.town || address.village || address.municipality || '';
    const state = address.state || address.region || address.province || '';
    const postal_code = address.postcode || address.postal_code || '';
    const country_name = address.country || '';
    const country_code = address.country_code?.toUpperCase() || '';

    // Build full address string
    const addressParts = [
      house_number && road ? `${house_number} ${road}` : road,
      suburb,
      city,
      state,
      postal_code,
      country_name
    ].filter(Boolean);

    const full_address = addressParts.join(', ');

    return {
      full_address,
      country_name,
      country_code,
      city,
      state,
      postal_code,
      latitude,
      longitude
    };

  } catch (error: any) {
    console.error('Location detection error:', error);

    // Handle specific geolocation errors
    if (error.code === 1) {
      return {
        type: 'permission_denied',
        message: 'Location access denied. Please enable location permissions and try again.'
      };
    } else if (error.code === 2) {
      return {
        type: 'position_unavailable',
        message: 'Location information is unavailable. Please enter your details manually.'
      };
    } else if (error.code === 3) {
      return {
        type: 'timeout',
        message: 'Location request timed out. Please try again or enter manually.'
      };
    } else {
      return {
        type: 'unknown',
        message: 'Failed to detect location. Please enter your details manually.'
      };
    }
  }
};

// Get list of countries with names and codes for dropdown
export const getCountriesList = () => {
  return [
    { name: 'Afghanistan', code: 'AF' },
    { name: 'Albania', code: 'AL' },
    { name: 'Algeria', code: 'DZ' },
    { name: 'Argentina', code: 'AR' },
    { name: 'Australia', code: 'AU' },
    { name: 'Austria', code: 'AT' },
    { name: 'Bangladesh', code: 'BD' },
    { name: 'Belgium', code: 'BE' },
    { name: 'Brazil', code: 'BR' },
    { name: 'Canada', code: 'CA' },
    { name: 'China', code: 'CN' },
    { name: 'Denmark', code: 'DK' },
    { name: 'Egypt', code: 'EG' },
    { name: 'France', code: 'FR' },
    { name: 'Germany', code: 'DE' },
    { name: 'Ghana', code: 'GH' },
    { name: 'Greece', code: 'GR' },
    { name: 'India', code: 'IN' },
    { name: 'Indonesia', code: 'ID' },
    { name: 'Ireland', code: 'IE' },
    { name: 'Italy', code: 'IT' },
    { name: 'Japan', code: 'JP' },
    { name: 'Kenya', code: 'KE' },
    { name: 'Malaysia', code: 'MY' },
    { name: 'Mexico', code: 'MX' },
    { name: 'Netherlands', code: 'NL' },
    { name: 'New Zealand', code: 'NZ' },
    { name: 'Nigeria', code: 'NG' },
    { name: 'Norway', code: 'NO' },
    { name: 'Pakistan', code: 'PK' },
    { name: 'Philippines', code: 'PH' },
    { name: 'Poland', code: 'PL' },
    { name: 'Portugal', code: 'PT' },
    { name: 'Russia', code: 'RU' },
    { name: 'Saudi Arabia', code: 'SA' },
    { name: 'Singapore', code: 'SG' },
    { name: 'South Africa', code: 'ZA' },
    { name: 'South Korea', code: 'KR' },
    { name: 'Spain', code: 'ES' },
    { name: 'Sri Lanka', code: 'LK' },
    { name: 'Sweden', code: 'SE' },
    { name: 'Switzerland', code: 'CH' },
    { name: 'Thailand', code: 'TH' },
    { name: 'Turkey', code: 'TR' },
    { name: 'Ukraine', code: 'UA' },
    { name: 'United Arab Emirates', code: 'AE' },
    { name: 'United Kingdom', code: 'GB' },
    { name: 'United States', code: 'US' },
    { name: 'Vietnam', code: 'VN' }
  ];
};

// Find country code by name (for manual entry fallback)
export const getCountryCodeByName = (countryName: string): string => {
  const countries = getCountriesList();
  const country = countries.find(c => 
    c.name.toLowerCase() === countryName.toLowerCase() ||
    c.name.toLowerCase().includes(countryName.toLowerCase()) ||
    countryName.toLowerCase().includes(c.name.toLowerCase())
  );
  return country?.code || '';
};